import { Button } from '@/client/components/ui/button';
import { toISODate } from '@/client/lib/dates';
import { cn } from '@/client/lib/utils';
import type { RouterOutputs } from '@/client/utils/trpc';
import { Plus } from 'lucide-react';

type Entry = RouterOutputs['timesheets']['listEntries'][number];

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface DayCardProps {
  date: string;
  entries: Entry[];
  isLocked: boolean;
  onAdd: (date: string) => void;
  onEdit: (entry: Entry) => void;
}

export function DayCard({ date, entries, isLocked, onAdd, onEdit }: DayCardProps) {
  const d = new Date(`${date}T00:00:00`);
  const dayOfWeek = d.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const isToday = date === toISODate(new Date());
  const totalHours = entries.reduce((s, e) => s + e.hours, 0);

  return (
    <div
      className={cn(
        'rounded-xl border border-border/70 bg-card shadow-soft',
        isToday && 'border-signal',
        isWeekend && 'bg-muted/30',
      )}
    >
      {/* Day header */}
      <div
        className={cn('flex items-center gap-3.5 px-5 py-3.5', entries.length > 0 && 'border-b')}
      >
        {/* Date stamp */}
        <div className="w-14 shrink-0 text-center">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            {DAY_ABBR[dayOfWeek]}
          </div>
          <div className="text-2xl font-semibold tabular-nums leading-tight">{d.getDate()}</div>
        </div>

        {/* Day info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[15px] font-semibold leading-tight">
              {isToday ? 'Today' : DAY_NAMES[dayOfWeek]}
            </p>
            {isToday && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">
                Today
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {entries.length === 0
              ? 'No time logged yet'
              : `${entries.length} ${entries.length === 1 ? 'entry' : 'entries'} · ${totalHours.toFixed(1)}h`}
          </p>
        </div>

        {/* Add entry button */}
        <Button
          variant={isToday && !isLocked ? 'default' : 'outline'}
          size="sm"
          type="button"
          className="shrink-0 gap-1.5"
          disabled={isLocked}
          onClick={() => onAdd(date)}
        >
          <Plus className="h-3.5 w-3.5" />
          Add entry
        </Button>
      </div>

      {/* Entries */}
      {entries.length > 0 && (
        <div className="py-1">
          {entries.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => onEdit(entry)}
              className="flex w-full items-center gap-3 px-5 py-2.5 pl-[76px] text-left hover:bg-secondary/50"
            >
              <div className="flex w-40 shrink-0 items-center gap-2 min-w-0">
                <span className="h-2 w-2 shrink-0 rounded-sm bg-signal" />
                <span className="truncate text-sm font-medium">{entry.project_name}</span>
              </div>
              <span className="flex-1 truncate text-sm text-muted-foreground">
                {entry.description}
              </span>
              <span className="shrink-0 text-sm font-semibold tabular-nums">
                {entry.hours.toFixed(1)}h
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
